import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { MaskedLines, Fade, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import { INSIGHT_CATEGORIES } from "@/data/content";

const Insights = () => {
  const [articles, setArticles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api
      .get("/insights")
      .then((res) => setArticles(res.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const filtered = category === "All" ? articles : articles.filter((a) => a.category === category);

  return (
    <>
      <SEO
        title="Insights | Mosaicon Strategies — Communications, Public Affairs & Communities"
        description="Perspectives on communications, communities, public affairs and the changing environment in which organizations operate."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 md:pt-28 md:pb-20" data-testid="insights-hero">
        <Overline>Insights</Overline>
        <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tighter text-ink" data-testid="insights-heading">
          <MaskedLines delay={0.15} lines={[<>Perspectives &<br className="hidden md:block" /> <em className="text-burgundy">analysis</em>.</>]} />
        </h1>
        <Fade delay={0.3}>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-ink/70" data-testid="insights-intro">
            Perspectives on communications, communities, public affairs and the changing environment in which
            organizations operate.
          </p>
        </Fade>
        <Fade delay={0.4} className="mt-10 flex flex-wrap gap-2" data-testid="insights-categories">
          {["All", ...INSIGHT_CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-xs uppercase tracking-[0.14em] px-4 py-2 border transition-colors duration-300 ${
                category === c
                  ? "bg-burgundy text-cream border-burgundy"
                  : "border-ink/20 text-ink/60 hover:border-burgundy hover:text-burgundy"
              }`}
            >
              {c}
            </button>
          ))}
        </Fade>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 md:pb-32" data-testid="insights-list">
        {loaded && filtered.length === 0 && (
          <Fade>
            <div className="border-t border-ink/15 pt-16 md:pt-24 max-w-3xl" data-testid="insights-empty">
              <p className="font-serif text-2xl md:text-4xl leading-snug tracking-tight text-ink/80">
                Our first perspectives are being prepared.
              </p>
              <p className="mt-6 text-base leading-relaxed text-ink/60">
                Articles on strategic communications, media relations, public affairs, community engagement,
                multicultural communications, political campaigns and reputation will be published here soon.
              </p>
            </div>
          </Fade>
        )}
        {filtered.length > 0 && (
          <div className="border-t border-ink/15">
            {filtered.map((a, i) => (
              <Fade key={a._id || a.id} delay={i * 0.05}>
                <Link
                  to={`/insights/${a.slug}`}
                  data-testid={`insight-article-${i + 1}`}
                  className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline py-10 border-b border-ink/15"
                >
                  <p className="md:col-span-2 text-xs uppercase tracking-[0.2em] text-burgundy">{a.category}</p>
                  <div className="md:col-span-9">
                    <h2 className="font-serif text-2xl md:text-4xl tracking-tight text-ink group-hover:text-burgundy transition-colors duration-300">
                      {a.title}
                    </h2>
                    {a.excerpt && <p className="mt-3 text-sm leading-relaxed text-ink/60 max-w-2xl">{a.excerpt}</p>}
                    <p className="mt-3 text-xs text-ink/40">
                      {new Date(a.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  </div>
                  <ArrowUpRight className="hidden md:block md:col-span-1 h-5 w-5 text-ink/30 transition-all duration-300 group-hover:text-burgundy group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              </Fade>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default Insights;
